import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Skeleton } from '@/components/skeleton'
import { Carousel } from '@/components/slider'
import { TestimonialsCard } from '@/components/testimonials/testimonials-card'
import { TestimonialsModal } from '@/components/testimonials/testimonials-modal'
import { useMediaQuery } from '@/hooks/use-media-query'
import { Testimonial } from '@/models/testimonial'
import api from '@/services/api'

interface GuardiansTestimonialsProps {
  title?: string
}

export function GuardiansTestimonials({ title }: GuardiansTestimonialsProps) {
  const [testimonials, setTestimonials] = useState<Testimonial[] | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedTestimonial, setSelectedTestimonial] =
    useState<Testimonial | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const showOne = useMediaQuery('(max-width: 880px)')
  const isFullHD = useMediaQuery('(min-width: 1600px)')

  useEffect(() => {
    const abortController = new AbortController()
    const signal = abortController.signal

    async function fetchTestimonials() {
      await api
        .get<Testimonial[]>('/depoimentos', { signal })
        .then((response) => setTestimonials(response.data))
        .catch((error) => toast.error(error.response.data.message))
        .finally(() => setIsLoading(false))
    }
    fetchTestimonials()

    return () => {
      abortController.abort()
    }
  }, [])

  const handleOpenModal = useCallback((testimonial: Testimonial) => {
    setSelectedTestimonial(testimonial)
    setIsModalOpen(true)
  }, [])

  if (isLoading) {
    return (
      <section className="space-y-8">
        <h2 className="title">{title || 'Depoimentos dos guardiões'}</h2>

        <div className="grid h-74.75 grid-cols-1 gap-6 px-8 pt-4 pb-6 sm:grid-cols-2 md:grid-cols-3 2xl:h-96 2xl:px-14 2xl:pt-6 2xl:pb-12">
          <Skeleton
            quantity={showOne ? 1 : !isFullHD ? 2 : 3}
            className="size-full rounded-2xl"
          />
        </div>
      </section>
    )
  }

  if (!testimonials) return null

  return (
    <section className="space-y-8">
      <h2 className="title">{title || 'Depoimentos dos guardiões'}</h2>

      <Carousel
        itemsPerPage={showOne ? 1 : !isFullHD ? 2 : 3}
        spaceBetween={24}
        carouselInsideButtons
        navigation={false}
        allItemsFit={!showOne && testimonials.length <= 2}
      >
        {testimonials.map((testimonial) => (
          <TestimonialsCard
            variant="slider"
            key={testimonial.id_depoimento}
            handleOpenModal={handleOpenModal}
            testimonial={testimonial}
          />
        ))}
      </Carousel>
      <TestimonialsModal
        personName={selectedTestimonial?.nome_pessoa || ''}
        profilePhoto={selectedTestimonial?.foto_pessoa || ''}
        testimonial={selectedTestimonial?.depoimento || ''}
        description={selectedTestimonial?.descricao_pessoa || ''}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
    </section>
  )
}
